import { qk } from "@/api/queryKeys";
import { useApiQuery } from "@/api/useApiQuery";
import { useUserKey } from "@/api/useUserKey";
import { getMetricDisciplineNotifications } from "@/service/reqUrl.service";

import type { DisciplineNotificationParams } from "./types";

/** The ETA notification metrics the SP returns, one row per (discipline ×
 *  interval) each. The first is what the by-Discipline card plots; the rest are
 *  the counts behind it, shown in the hover details. */
export const ETA_ATTAINMENT_METRIC_CODE = "ETA_PercentEligibleTextsSent";
export const ETA_TEXTS_SENT_METRIC_CODE = "ETA_TextSent";
export const ETA_OPTED_IN_METRIC_CODE = "ETA_VisitsOptedInToTexting";
export const ETA_VISITS_COMPLETED_METRIC_CODE = "ETA_VisitsCompleted";

interface UseMetricDisciplineNotificationsResult {
  /** Raw response payload, kept UNSHAPED — `toDisciplineSeries` maps the SP's
   *  `{ DisciplineSummaryValues: [...] }` document for the card. */
  data: unknown;
  loading: boolean;
  error: string | null;
}

/**
 * Fires `POST /dashboard/metric-discipline-notifications` for the Patient
 * Notifications dashboard, AFTER the dashboard-metrics call has completed — the
 * caller passes `params: null` until then, so the request only goes out once the
 * metrics land.
 *
 * `metricCodes` is the card's metric (e.g. "ETA_PercentEligibleTextsSent");
 * `summarizeByInterval` picks the bucketing — 1 = the interval the date
 * range implies (one bucket per discipline: the card's Snapshot mode), 0 = the
 * next interval down (the series its Trend mode plots). The dashboard's filters
 * reach the SP as `filters` (its JSON `@Filters`). Re-fires when the date range
 * or any filter in `params` changes.
 *
 * CALLED TWICE by `PillarPage` — once per mode, since the flag is part of the
 * body and therefore of the query key. The Snapshot call fires with the page;
 * the Trend call waits until the user opens that mode and is cached afterwards.
 *
 * NOTE: `PillarPage` gates the By-Clinician call on the SNAPSHOT call's
 * `data !== null` (an empty array still counts as done). A cached response
 * satisfies that immediately, so revisiting Patient Notifications now resolves
 * the whole chain without a round-trip per step.
 */
export function useMetricDisciplineNotifications(
  params: DisciplineNotificationParams | null,
): UseMetricDisciplineNotificationsResult {
  const userKey = useUserKey();

  const { data, loading, error } = useApiQuery<unknown>({
    queryKey: qk.disciplineNotifications(userKey, params),
    url: getMetricDisciplineNotifications,
    body: params,
    enabled: params != null,
    fallbackMessage: "Failed to fetch discipline notifications",
  });

  return { data, loading, error };
}
